import { useState } from 'react';
import {
  getRegionLabel,
  formatExactShareMoney,
  formatUsd,
  isMultiMarketView,
} from '../data/countries';
import {
  getMarketListingPresentation,
  filterMarketListingsForView,
} from '../utils/properties';
import { computeMarketplaceTradeFees } from '../utils/marketplace';
import { MARKETPLACE_IMAGE_FALLBACK } from '../config/constants';
import { PROPERTIES } from '../data/demoData';
import useListPagination from '../hooks/useListPagination';
import PaginationBar from '../components/shared/PaginationBar';
import CountryFlag from '../components/shared/CountryFlag';
import PageRefinedHero from '../components/layout/PageRefinedHero';

const MarketplacePage = ({ listings, catalogProperties, balance, onBuy, onSelectProperty, country }) => {
  const [filter, setFilter] = useState("All");
  const catalog = catalogProperties?.length ? catalogProperties : PROPERTIES;
  const filtered = filterMarketListingsForView(listings, catalog, { country, typeFilter: filter });
  const regionLabel = getRegionLabel(country);
  const marketPaginationKey = `market|${filter}|${country}|${filtered.length}`;
  const {
    items: marketPageItems,
    page: marketPage,
    total: marketTotal,
    totalPages: marketTotalPages,
    startIndex: marketStart,
    endIndex: marketEnd,
    hasPrev: marketHasPrev,
    hasNext: marketHasNext,
    goToPage: marketGoToPage
  } = useListPagination(filtered, marketPaginationKey);

  const renderMarketCard = (listing) => {
    const pres = getMarketListingPresentation(catalog, listing);
    const p = pres.property;
    const discountPct = ((listing.pricePerShare - listing.originalPrice) / listing.originalPrice) * 100;
    const discountLabel = discountPct.toFixed(1);
    const { buyerPaysUsd } = computeMarketplaceTradeFees(listing.pricePerShare, 1);
    const canAfford = balance + 0.001 >= buyerPaysUsd;
    const hasPhoto = Boolean(pres.image && pres.image !== MARKETPLACE_IMAGE_FALLBACK);
    return (
      <div
        key={listing.id}
        className="market-card"
        onClick={() => p && onSelectProperty(p)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" && p) onSelectProperty(p); }}
      >
        <div className={`market-card-image${hasPhoto ? "" : " market-card-image--placeholder"}`}>
          {hasPhoto ? (
            <img src={pres.image} alt={pres.title} loading="lazy" />
          ) : (
            <i className="fas fa-building" aria-hidden="true"></i>
          )}
          <span className="property-badge" style={{ top: "0.75rem", left: "0.75rem", fontSize: "0.75rem", padding: "0.25rem 0.7rem" }}>{pres.type}</span>
          {pres.galleryCount > 1 && (
            <div className="photo-count"><i className="fas fa-images" style={{ marginRight: "0.3rem" }}></i>{pres.galleryCount} photos</div>
          )}
        </div>
        <div className="market-card-body">
          <div className="agent-header">
            <div className="agent-company">
              <div className="agent-logo">{listing.sellerInitial}</div>
              <div>
                <div className="user-name">{listing.seller}</div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-light)" }}>Listed {listing.listedDate}</div>
              </div>
            </div>
            {discountPct < 0 ? (
              <span className="status-badge status-approved">{discountLabel}%</span>
            ) : (
              <span className="status-badge status-pending">+{discountLabel}%</span>
            )}
          </div>
          <h4 style={{ marginBottom: "0.3rem", fontSize: "1rem" }}>{pres.title}</h4>
          <p style={{ color: "var(--text-light)", fontSize: "0.85rem", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
            {isMultiMarketView(country) && pres.country && (
              <CountryFlag code={pres.country} imgWidth={16} emojiSize="0.95rem" />
            )}
            <span>
              <i className="fas fa-map-marker-alt" style={{ color: "var(--accent)", marginRight: "0.3rem" }}></i>
              {pres.location || regionLabel}
            </span>
          </p>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <div>
              <div style={{ fontSize: "0.75rem", color: "var(--text-light)" }}>Shares Available</div>
              <div style={{ fontWeight: 700 }}>{listing.shares}</div>
            </div>
            <div>
              <div style={{ fontSize: "0.75rem", color: "var(--text-light)" }}>Price/Share</div>
              <div style={{ fontWeight: 700, color: "var(--primary)" }}>{formatExactShareMoney(listing.pricePerShare, country)}</div>
              <div style={{ fontSize: "0.7rem", color: "var(--text-light)" }}>{formatUsd(listing.pricePerShare)} USD</div>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: "100%" }}
            disabled={!canAfford}
            onClick={(e) => { e.stopPropagation(); canAfford && onBuy(listing); }}
          >
            {canAfford ? "Buy Shares" : "Insufficient Funds"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="coown-page coown-page--hero" data-listings-anchor>
      <PageRefinedHero
        badge="Secondary market"
        title="Trade shares"
        titleAccent="peer-to-peer"
        subtitle={`Buy fractional stakes from other investors in ${regionLabel}. Prices display in your selected currency; share cost is charged in USD.`}
        benefits={[
          { icon: 'fas fa-exchange-alt', title: 'Investor-to-investor', subtitle: 'Purchase shares others have listed' },
          { icon: 'fas fa-unlock', title: 'No lock-up', subtitle: 'List or buy when it suits you' },
          { icon: 'fas fa-tag', title: 'Market pricing', subtitle: 'See discounts and premiums live' },
        ]}
      />

      <div className="coown-page-body">
        <div className="coown-page-toolbar">
          <div className="coown-page-toolbar__lead">
            <h2>{regionLabel} listings</h2>
            <p>{filtered.length} {filtered.length === 1 ? 'offer' : 'offers'} available now</p>
          </div>
          <div className="coown-page-toolbar__actions filters">
            {["All", "Residential", "Commercial", "Vacation"].map((f) => (
              <button key={f} className={`filter-btn ${filter === f ? "active" : ""}`} onClick={() => setFilter(f)}>{f}</button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="coown-page-empty">
            <i className="fas fa-store coown-page-empty__icon" aria-hidden="true"></i>
            <h3>No secondary listings in {regionLabel}</h3>
            <p>
              {filter !== "All"
                ? `No ${filter.toLowerCase()} share listings in this market right now. Try "All" or switch country in the header.`
                : `Investors have not listed shares for properties in ${regionLabel} yet. Try another country or browse primary offerings on the home page.`}
            </p>
          </div>
        ) : (
          <>
            <div className="properties-grid">
              {marketPageItems.map((listing) => renderMarketCard(listing))}
            </div>
            <PaginationBar
              page={marketPage}
              totalPages={marketTotalPages}
              total={marketTotal}
              startIndex={marketStart}
              endIndex={marketEnd}
              hasPrev={marketHasPrev}
              hasNext={marketHasNext}
              onPageChange={marketGoToPage}
              itemLabel="listings"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default MarketplacePage;
